import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { rtlApi, type EffectiveRtl } from '@/api/rtl.api';
import { useAuth } from '@/auth/useAuth';
import baseTheme from '@/theme/theme';

/** System default before the tenant/user RTL config resolves. */
const DEFAULT: EffectiveRtl = {
  direction: 'ltr', isRtl: false, textAlign: 'left', fontFamily: null, numberDirection: 'LTR', layoutMirroring: true, forced: false,
};

export interface DirectionContextValue {
  direction: 'ltr' | 'rtl';
  isRtl: boolean;
  font?: string | null;
  /** Whether the layout mirrors (nav/icons/padding) in RTL. */
  mirroring: boolean;
  /** True when the tenant forces RTL regardless of language. */
  forced: boolean;
  isReady: boolean;
}

const DirectionContext = createContext<DirectionContextValue | null>(null);

/**
 * Global RTL engine (Phase 14.7, STEP 1/2/10/12). Resolves the effective direction + font from the backend
 * (/rtl/effective — tenant force → user override → language default), applies it to the document and to a
 * direction-aware MUI theme at runtime — no reload. Single source of direction truth; nothing is hardcoded.
 */
export function RtlProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const query = useQuery({
    queryKey: ['rtl', 'effective'],
    queryFn: rtlApi.effective,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const eff = query.data ?? DEFAULT;
  const direction = eff.direction === 'rtl' ? 'rtl' : 'ltr';
  const font = eff.fontFamily ?? null;

  // Apply direction + font to the document (drives text alignment, flexbox flow, screen-reader reading order).
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    if (font) document.documentElement.style.setProperty('--app-font', font);
    else document.documentElement.style.removeProperty('--app-font');
  }, [direction, font]);

  // Direction-aware theme — MUI components read theme.direction (Drawer, Tabs, inputs, menus…). Font applied when configured.
  const theme = useMemo(
    () => createTheme(baseTheme, {
      direction,
      ...(font ? { typography: { fontFamily: `${font}, ${baseTheme.typography.fontFamily}` } } : {}),
    }),
    [direction, font],
  );

  const value = useMemo<DirectionContextValue>(() => ({
    direction, isRtl: direction === 'rtl', font, mirroring: eff.layoutMirroring, forced: eff.forced,
    isReady: !isAuthenticated || query.isSuccess,
  }), [direction, font, eff.layoutMirroring, eff.forced, isAuthenticated, query.isSuccess]);

  return (
    <DirectionContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </DirectionContext.Provider>
  );
}

/** Read the resolved direction anywhere. Reuses the global RTL context — no duplicate state. */
export function useDirection(): DirectionContextValue {
  const ctx = useContext(DirectionContext);
  if (!ctx) throw new Error('useDirection must be used within <RtlProvider>.');
  return ctx;
}
